import { Fragment, useMemo, useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AppIcon } from '../ui/icon'
import { $api } from '../../api/queryClient'
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs'
import { useCan } from '../../contexts/PermissionsContext'
import { useFormatters } from '../../lib/format'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Alert, AlertDescription } from '../ui/alert'
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../ui/dropdown-menu'
import { AssetPicker, isAssetPickerEscapeToClear } from '../AssetPicker'
import type { ChannelEndpoint } from '../../api/schema'
import { residentIntakeUrl } from '../../hooks/useResidentIntake'
import { PageHeader } from '../shared/PageHeader'
import { SECTION_ICONS } from '../../lib/section-icons'
import { EmptyState } from '../ui/empty-state'
import { mdiAt, mdiCellphone, mdiCheck, mdiChevronLeft, mdiChevronRight, mdiCloseCircleOutline, mdiContentCopy, mdiDotsHorizontal, mdiEmailOutline, mdiLoading, mdiMessageOutline, mdiPlus, mdiUndo } from '@mdi/js'

/**
 * The intake addresses, as a PAGE.
 *
 * Managing where mail can arrive is configuration, and the rest of the
 * configuration lives in pages — a modal was the wrong container for a list
 * that grows with the portfolio (spec 2026-09-09-13). Being a page is what buys
 * the URL, the breadcrumb, browser back, and filters that can be shared as a
 * link.
 *
 * An intake address is a public, right-less identifier — writing to it gives
 * nothing beyond dropping a message in a queue nobody reads without being
 * invited — which is exactly what makes it printable in an AG convocation and
 * on a notice board. Opening one is idempotent per site, because an address
 * that changed because someone clicked twice would be an address already
 * printed on a wall; and closing one never deletes it, because an address that
 * was printed somewhere has to stay resolvable to "this was ours, it is
 * closed".
 *
 * Web labels (`web`) are created by the equipment QR flow and only LISTED and
 * CLOSED here — this page deliberately carries no affordance to create one.
 */

const CHANNEL_ICON = {
    email: mdiEmailOutline,
    whatsapp: mdiCellphone,
    web: mdiMessageOutline,
} as const

const CHANNELS = ['email', 'whatsapp', 'web'] as const
const SCOPES = ['organization', 'asset'] as const

export interface IntakeAddressesSearch {
    q?: string
    channel?: 'email' | 'whatsapp' | 'web'
    scope?: 'organization' | 'asset'
    closed?: boolean
    limit?: number
    offset?: number
}

// The server's own default. Kept here so the pager can compute the next offset
// without waiting for the response to tell it how big a page is.
const DEFAULT_PAGE_SIZE = 100

export function IntakeAddressesPage({
    org,
    search,
}: {
    org: string
    search: IntakeAddressesSearch
}) {
    const { t } = useTranslation('conversations')
    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const { formatDate } = useFormatters()
    const canWrite = useCan('conversations.write')

    const [pickingSite, setPickingSite] = useState(false)
    const [copied, setCopied] = useState<string | null>(null)
    const [closing, setClosing] = useState<ChannelEndpoint | null>(null)

    const includeClosed = search.closed === true
    const pageSize = search.limit && search.limit > 0 ? search.limit : DEFAULT_PAGE_SIZE
    const offset = search.offset && search.offset > 0 ? search.offset : 0

    // Every filter is URL state, so a filtered view is a shareable link.
    // Changing anything but the offset sends the reader back to the first page:
    // a narrower filter on page three otherwise lands on an empty screen.
    const setSearch = (patch: Partial<IntakeAddressesSearch>, options?: { replace?: boolean }) => {
        void navigate({
            to: '/$org/conversations/intake-addresses',
            params: { org },
            search: (previous: IntakeAddressesSearch): IntakeAddressesSearch => {
                const next = { ...previous, ...patch }
                const nextOffset = 'offset' in patch ? next.offset : 0

                return {
                    q: next.q || undefined,
                    channel: next.channel || undefined,
                    scope: next.scope || undefined,
                    closed: next.closed || undefined,
                    limit: next.limit || undefined,
                    offset: nextOffset || undefined,
                }
            },
            replace: options?.replace,
        })
    }

    const list = $api.useQuery('get', '/api/v1/organizations/{orgCode}/channel-endpoints', {
        params: {
            path: { orgCode: org },
            query: {
                q: search.q || undefined,
                channel: search.channel || undefined,
                scope: search.scope || undefined,
                include_inactive: includeClosed || undefined,
                limit: pageSize,
                offset: offset || undefined,
            },
        },
    })

    const invalidate = () =>
        queryClient.invalidateQueries({
            predicate: (query) => {
                const key = query.queryKey as unknown[]

                return (
                    key.length >= 2
                    && key[0] === 'get'
                    && key[1] === '/api/v1/organizations/{orgCode}/channel-endpoints'
                )
            },
        })

    const create = $api.useMutation('post', '/api/v1/organizations/{orgCode}/channel-endpoints', {
        onSuccess: () => {
            setPickingSite(false)
            void invalidate()
        },
    })

    const setActive = $api.useMutation(
        'patch',
        '/api/v1/organizations/{orgCode}/channel-endpoints/{endpointUid}',
        { onSuccess: () => void invalidate() },
    )

    useBreadcrumbs([
        { title: t('nav'), path: `/${org}/conversations` },
        { title: t('intake_addresses.title') },
    ])

    const payload = list.data as { data?: ChannelEndpoint[]; total?: number } | undefined
    // Memoized because the grouping below keys on it: `?? []` would hand a new
    // array identity to the memo on every render.
    const endpoints = useMemo(() => (payload?.data ?? []) as ChannelEndpoint[], [payload])
    const total = payload?.total ?? endpoints.length

    // The server already returns the rows grouped (organization first, then one
    // block per site); all this does is insert the header row between blocks.
    const groups = useMemo(() => {
        const out: { key: string; label: string; assetUid?: string; rows: ChannelEndpoint[] }[] = []
        for (const endpoint of endpoints) {
            const asset = endpoint.asset as { uid?: string; name?: string } | null | undefined
            const key = asset?.uid ?? '__organization__'
            const label = asset?.name
                ? String(asset.name)
                : t('intake_addresses.group_organization')
            const last = out[out.length - 1]
            if (last && last.key === key) {
                last.rows.push(endpoint)
            } else {
                out.push({ key, label, assetUid: asset?.uid, rows: [endpoint] })
            }
        }

        return out
    }, [endpoints, t])

    // What the copy button hands back (spec 2026-09-11-11).
    //
    // For `email` and `whatsapp` the address column IS the connection detail:
    // you can write to it as it stands. For a LABEL-BACKED `web` address it is
    // a bare identifier a resident can do nothing with, so the button hands
    // back that address's own public page instead — the page header promises
    // something printable in an AG convocation, and an identifier is not.
    //
    // The desk, voice and generated web addresses keep returning the bare
    // identifier: they have no public page by design, and support and the logs
    // still refer to them that way.
    const copyValue = (endpoint: ChannelEndpoint) =>
        residentIntakeUrl(org, endpoint.channel, endpoint.address) ||
        endpoint.email_address ||
        endpoint.address ||
        ''

    const copy = async (endpoint: ChannelEndpoint) => {
        const value = copyValue(endpoint)
        if (!value) return
        try {
            await navigator.clipboard.writeText(value)
            setCopied(endpoint.uid ?? null)
            setTimeout(() => setCopied(null), 1500)
        } catch {
            // A browser that refuses clipboard access is not an error worth a
            // banner: the address is on screen and selectable.
        }
    }

    const filtering = Boolean(search.q || search.channel || search.scope)

    const cell = 'block md:table-cell px-3 py-1 md:py-2 align-middle'

    return (
        <div className="space-y-4" data-testid="intake-addresses-page">
            <PageHeader
                icon={SECTION_ICONS.intakeAddresses}
                title={t('intake_addresses.title')}
                description={t('endpoints.description')}
                actions={
                <>
                {canWrite && (
                    <div className="flex flex-wrap gap-2">
                        <Button
                            variant="outline"
                            disabled={create.isPending}
                            onClick={() =>
                                create.mutate({
                                    params: { path: { orgCode: org } },
                                    body: { channel: 'email' },
                                })
                            }
                            aria-label={t('endpoints.create_organization')}
                            title={t('endpoints.create_organization')}
                            data-testid="endpoint-create-organization"
                        >
                            <AppIcon path={mdiPlus} size="xs" className="mr-1" />
                            {t('endpoints.create_organization')}
                        </Button>
                        <Button
                            variant="outline"
                            onClick={() => setPickingSite(true)}
                            aria-label={t('endpoints.create_site')}
                            title={t('endpoints.create_site')}
                            data-testid="endpoint-create-site"
                        >
                            <AppIcon path={mdiPlus} size="xs" className="mr-1" />
                            {t('endpoints.create_site')}
                        </Button>
                    </div>
                )}
                </>
                }
            />

            {/* Toolbar. Everything here writes to the URL. */}
            <div className="flex flex-wrap items-center gap-2" data-testid="intake-addresses-toolbar">
                <Input
                    className="h-8 w-full sm:w-64 text-sm"
                    value={search.q ?? ''}
                    onChange={(event) => setSearch({ q: event.target.value }, { replace: true })}
                    placeholder={t('intake_addresses.search_placeholder')}
                    aria-label={t('intake_addresses.search_label')}
                    data-testid="intake-addresses-search"
                />
                <div className="flex flex-wrap gap-1" role="group" aria-label={t('channel.label')}>
                    <Button
                        variant={search.channel ? 'outline' : 'secondary'}
                        size="sm"
                        className="h-8"
                        aria-pressed={!search.channel}
                        onClick={() => setSearch({ channel: undefined })}
                        data-testid="intake-addresses-channel-all"
                    >
                        {t('channel.all')}
                    </Button>
                    {CHANNELS.map((channel) => {
                        const channelIcon = CHANNEL_ICON[channel]

                        return (
                            <Button
                                key={channel}
                                variant={search.channel === channel ? 'secondary' : 'outline'}
                                size="sm"
                                className="h-8"
                                aria-pressed={search.channel === channel}
                                onClick={() => setSearch({ channel })}
                                data-testid={`intake-addresses-channel-${channel}`}
                            >
                                <AppIcon path={channelIcon} size="xs" className="mr-1" />
                                {t(`channel.${channel}`)}
                            </Button>
                        )
                    })}
                </div>
                <div
                    className="flex flex-wrap gap-1"
                    role="group"
                    aria-label={t('intake_addresses.scope_label')}
                >
                    <Button
                        variant={search.scope ? 'outline' : 'secondary'}
                        size="sm"
                        className="h-8"
                        aria-pressed={!search.scope}
                        onClick={() => setSearch({ scope: undefined })}
                        data-testid="intake-addresses-scope-all"
                    >
                        {t('intake_addresses.scope_all')}
                    </Button>
                    {SCOPES.map((scope) => (
                        <Button
                            key={scope}
                            variant={search.scope === scope ? 'secondary' : 'outline'}
                            size="sm"
                            className="h-8"
                            aria-pressed={search.scope === scope}
                            onClick={() => setSearch({ scope })}
                            data-testid={`intake-addresses-scope-${scope}`}
                        >
                            {t(`intake_addresses.scope_${scope}`)}
                        </Button>
                    ))}
                </div>
                <Button
                    variant={includeClosed ? 'secondary' : 'outline'}
                    size="sm"
                    className="h-8"
                    aria-pressed={includeClosed}
                    onClick={() => setSearch({ closed: includeClosed ? undefined : true })}
                    data-testid="intake-addresses-include-closed"
                >
                    {t('intake_addresses.include_closed')}
                </Button>
                <span className="text-xs text-muted-foreground ml-auto" data-testid="intake-addresses-count">
                    {t('intake_addresses.count', { shown: endpoints.length, total })}
                </span>
            </div>

            {list.isError && (
                <Alert variant="destructive" data-testid="intake-addresses-error">
                    <AlertDescription>{t('intake_addresses.error')}</AlertDescription>
                </Alert>
            )}

            <div className="border rounded-lg overflow-x-auto bg-card">
                <table className="w-full text-sm" data-testid="conversations-endpoints-list">
                    <thead className="hidden md:table-header-group border-b bg-muted/40">
                        <tr className="text-left text-xs text-muted-foreground">
                            <th className="px-3 py-2 font-medium">{t('intake_addresses.column_channel')}</th>
                            <th className="px-3 py-2 font-medium">{t('intake_addresses.column_address')}</th>
                            <th className="px-3 py-2 font-medium">{t('intake_addresses.column_scope')}</th>
                            <th className="px-3 py-2 font-medium">{t('intake_addresses.column_conversations')}</th>
                            <th className="px-3 py-2 font-medium">{t('intake_addresses.column_last_message')}</th>
                            <th className="px-3 py-2 font-medium">{t('intake_addresses.column_created')}</th>
                            <th className="px-3 py-2 font-medium text-right">
                                {t('intake_addresses.column_actions')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="block md:table-row-group divide-y">
                        {list.isLoading && (
                            <tr className="block md:table-row">
                                <td className="block md:table-cell px-3 py-6 text-center text-muted-foreground" colSpan={7}>
                                    <AppIcon path={mdiLoading} size="sm" className="motion-safe:animate-spin inline-block" />
                                </td>
                            </tr>
                        )}
                        {!list.isLoading && endpoints.length === 0 && (
                            <tr className="block md:table-row">
                                <td className="block md:table-cell px-3" colSpan={7}>
                                    <EmptyState
                                        variant="inline"
                                        live
                                        icon={mdiAt}
                                        title={
                                            filtering || includeClosed
                                                ? t('intake_addresses.empty')
                                                : t('intake_addresses.empty_none')
                                        }
                                        data-testid="intake-addresses-empty"
                                    />
                                </td>
                            </tr>
                        )}
                        {groups.map((group) => (
                            <Fragment key={group.key}>
                                <tr
                                    className="block md:table-row bg-muted/30"
                                    data-testid={`intake-addresses-group-${group.key}`}
                                >
                                    <td
                                        className="block md:table-cell px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide"
                                        colSpan={7}
                                    >
                                        {group.assetUid ? (
                                            <Link
                                                to="/$org/assets/$assetUid"
                                                params={{ org, assetUid: group.assetUid }}
                                                className="hover:underline"
                                                data-testid={`intake-addresses-group-link-${group.key}`}
                                            >
                                                {group.label}
                                            </Link>
                                        ) : (
                                            group.label
                                        )}
                                    </td>
                                </tr>
                                {group.rows.map((endpoint) => {
                                    const endpointIcon = CHANNEL_ICON[
                                        (endpoint.channel ?? 'email') as keyof typeof CHANNEL_ICON
                                    ] ?? mdiEmailOutline
                                    const value = endpoint.email_address || endpoint.address || ''
                                    const closed = endpoint.active === false
                                    const asset = endpoint.asset as { uid?: string; name?: string } | null | undefined

                                    return (
                                        <tr
                                            key={endpoint.uid}
                                            className={`block md:table-row py-2 md:py-0 ${closed ? 'opacity-60' : ''}`}
                                            data-testid={`endpoint-${endpoint.uid}`}
                                            data-closed={closed ? 'true' : 'false'}
                                        >
                                            <td className={cell}>
                                                <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                                                    <AppIcon path={endpointIcon} size="xs" />
                                                    {t(`channel.${endpoint.channel ?? 'email'}`)}
                                                </span>
                                            </td>
                                            <td className={cell}>
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span
                                                        className={`font-mono text-xs md:text-sm break-all ${closed ? 'line-through text-muted-foreground' : 'text-foreground'}`}
                                                        data-testid={`endpoint-address-${endpoint.uid}`}
                                                    >
                                                        {value}
                                                    </span>
                                                    {closed && (
                                                        <Badge variant="outline" className="text-[10px]">
                                                            {t('intake_addresses.closed')}
                                                        </Badge>
                                                    )}
                                                </div>
                                                {endpoint.channel === 'web' && (
                                                    <div className="text-[11px] text-muted-foreground">
                                                        {t('intake_addresses.web_label')}
                                                    </div>
                                                )}
                                            </td>
                                            <td className={cell}>
                                                {asset?.uid ? (
                                                    <Link
                                                        to="/$org/assets/$assetUid"
                                                        params={{ org, assetUid: asset.uid }}
                                                        className="text-xs hover:underline"
                                                        data-testid={`endpoint-scope-${endpoint.uid}`}
                                                    >
                                                        {String(asset.name ?? t('endpoints.site'))}
                                                    </Link>
                                                ) : (
                                                    <span
                                                        className="text-xs text-muted-foreground"
                                                        data-testid={`endpoint-scope-${endpoint.uid}`}
                                                    >
                                                        {t('endpoints.organization')}
                                                    </span>
                                                )}
                                            </td>
                                            <td className={cell}>
                                                <span
                                                    className="text-xs tabular-nums"
                                                    data-testid={`endpoint-conversations-${endpoint.uid}`}
                                                >
                                                    <span className="md:hidden text-muted-foreground mr-1">
                                                        {t('intake_addresses.column_conversations')}:
                                                    </span>
                                                    {endpoint.conversation_count ?? 0}
                                                </span>
                                            </td>
                                            <td className={cell}>
                                                <span
                                                    className="text-xs text-muted-foreground"
                                                    data-testid={`endpoint-last-inbound-${endpoint.uid}`}
                                                >
                                                    <span className="md:hidden mr-1">
                                                        {t('intake_addresses.column_last_message')}:
                                                    </span>
                                                    {endpoint.last_inbound_at
                                                        ? formatDate(endpoint.last_inbound_at, {
                                                            dateStyle: 'medium',
                                                        })
                                                        : t('intake_addresses.never')}
                                                </span>
                                            </td>
                                            <td className={cell}>
                                                <span className="text-xs text-muted-foreground">
                                                    <span className="md:hidden mr-1">
                                                        {t('intake_addresses.column_created')}:
                                                    </span>
                                                    {endpoint.created_at
                                                        ? formatDate(endpoint.created_at, { dateStyle: 'medium' })
                                                        : '—'}
                                                </span>
                                            </td>
                                            <td className={`${cell} md:text-right`}>
                                                <div className="flex items-center gap-1 md:justify-end">
                                                    <Button
                                                        variant="outline"
                                                        size="icon"
                                                        className="h-7 w-7"
                                                        onClick={() => void copy(endpoint)}
                                                        aria-label={t('intake.copy')}
                                                        title={t('intake.copy')}
                                                        data-testid={`endpoint-copy-${endpoint.uid}`}
                                                    >
                                                        {copied === endpoint.uid ? (
                                                            <AppIcon path={mdiCheck} size="xs" />
                                                        ) : (
                                                            <AppIcon path={mdiContentCopy} size="xs" />
                                                        )}
                                                    </Button>
                                                    {/* Close and Reopen live in a row menu: they are
                                                        the row's actions, not per-cell affordances like
                                                        the copy button, and a menu keeps them reachable
                                                        on the stacked mobile layout. */}
                                                    {canWrite && (
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button
                                                                    variant="outline"
                                                                    size="icon"
                                                                    className="h-7 w-7"
                                                                    disabled={setActive.isPending}
                                                                    aria-label={t('intake_addresses.actions_menu')}
                                                                    title={t('intake_addresses.actions_menu')}
                                                                    data-testid={`endpoint-menu-${endpoint.uid}`}
                                                                >
                                                                    <AppIcon path={mdiDotsHorizontal} size="xs" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                {!closed && (
                                                                    <DropdownMenuItem
                                                                        // Deferred by a tick: opening the
                                                                        // AlertDialog while the menu is still
                                                                        // unmounting leaves Radix's
                                                                        // `pointer-events: none` on the body
                                                                        // and the confirm becomes unclickable.
                                                                        onSelect={() => {
                                                                            setTimeout(() => setClosing(endpoint), 0)
                                                                        }}
                                                                        data-testid={`endpoint-close-${endpoint.uid}`}
                                                                    >
                                                                        <AppIcon path={mdiCloseCircleOutline} size="xs" className="mr-2" />
                                                                        {t('intake_addresses.close')}
                                                                    </DropdownMenuItem>
                                                                )}
                                                                {closed && (
                                                                    <DropdownMenuItem
                                                                        onSelect={() =>
                                                                            setActive.mutate({
                                                                                params: {
                                                                                    path: {
                                                                                        orgCode: org,
                                                                                        endpointUid: endpoint.uid as string,
                                                                                    },
                                                                                },
                                                                                body: { active: true },
                                                                            })
                                                                        }
                                                                        data-testid={`endpoint-reopen-${endpoint.uid}`}
                                                                    >
                                                                        <AppIcon path={mdiUndo} size="xs" className="mr-2" />
                                                                        {t('intake_addresses.reopen')}
                                                                    </DropdownMenuItem>
                                                                )}
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* The list is bounded server-side, so it must say where it stops
                and let the reader walk past it — a portfolio that crosses one
                page would otherwise lose every address after the hundredth
                with nothing on screen saying so. */}
            {!list.isLoading && (offset > 0 || total > endpoints.length) && (
                <div
                    className="flex items-center justify-between gap-2 flex-wrap"
                    data-testid="intake-addresses-pager"
                >
                    <span className="text-sm text-muted-foreground" data-testid="intake-addresses-showing">
                        {t('intake_addresses.showing', {
                            from: endpoints.length === 0 ? 0 : offset + 1,
                            to: offset + endpoints.length,
                            total,
                        })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={offset === 0}
                            onClick={() => setSearch({ offset: Math.max(0, offset - pageSize) })}
                            data-testid="intake-addresses-previous"
                        >
                            <AppIcon path={mdiChevronLeft} size="xs" tone="muted" className="mr-1" />
                            {t('intake_addresses.previous')}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={offset + endpoints.length >= total}
                            onClick={() => setSearch({ offset: offset + pageSize })}
                            data-testid="intake-addresses-next"
                        >
                            {t('intake_addresses.next')}
                            <AppIcon path={mdiChevronRight} size="xs" tone="muted" className="ml-1" />
                        </Button>
                    </div>
                </div>
            )}

            {/* One short form: the right use of a modal. */}
            <Dialog open={pickingSite} onOpenChange={setPickingSite}>
                <DialogContent
                    className="max-w-lg"
                    onEscapeKeyDown={(event) => {
                        // Let Escape clear the asset picker's search term first
                        // (Radix would otherwise close the dialog because its
                        // Escape listener runs in document-capture, before the
                        // picker's own Input onKeyDown).
                        if (isAssetPickerEscapeToClear(event)) event.preventDefault()
                    }}
                >
                    <DialogHeader>
                        <DialogTitle>{t('endpoints.create_site')}</DialogTitle>
                        <DialogDescription>{t('endpoints.asset_help')}</DialogDescription>
                    </DialogHeader>
                    <AssetPicker
                        org={org}
                        testId="endpoint-asset-picker"
                        assetTypeCodes={['building_group', 'building', 'house', 'parcel_land']}
                        help={t('endpoints.asset_help')}
                        onChange={(assetUid) =>
                            create.mutate({
                                params: { path: { orgCode: org } },
                                body: { channel: 'email', asset_uid: assetUid },
                            })
                        }
                    />
                </DialogContent>
            </Dialog>

            <AlertDialog open={Boolean(closing)} onOpenChange={(open) => !open && setClosing(null)}>
                <AlertDialogContent data-testid="endpoint-close-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('intake_addresses.confirm_close_title')}</AlertDialogTitle>
                        {/* A web label is not a mailbox: closing it retires a
                            QR code that is glued to a wall, and its public page
                            starts answering "no longer in service" (spec
                            2026-09-10-01). Saying "mail stops being accepted"
                            there would describe the wrong consequence. */}
                        <AlertDialogDescription>
                            {t(
                                closing?.channel === 'web'
                                    ? 'intake_addresses.confirm_close_description_web'
                                    : 'intake_addresses.confirm_close_description',
                                {
                                    address: closing?.email_address || closing?.address || '',
                                },
                            )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="endpoint-close-cancel">
                            {t('intake_addresses.cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="endpoint-close-confirm-action"
                            onClick={() => {
                                if (!closing?.uid) return
                                setActive.mutate({
                                    params: {
                                        path: { orgCode: org, endpointUid: closing.uid as string },
                                    },
                                    body: { active: false },
                                })
                                setClosing(null)
                            }}
                        >
                            {t('intake_addresses.confirm_close_action')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    )
}
